import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { cn } from '../../../../lib/cn';
import { useTheme } from '../../../../context/ThemeContext';

const MenuContext = createContext<{
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
} | null>(null);

export function Menu({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <MenuContext.Provider value={{ open, setOpen }}>
      <div className="relative inline-block" ref={ref}>
        {children}
      </div>
    </MenuContext.Provider>
  );
}

export function MenuTrigger({ children, className }: { children: React.ReactNode, className?: string }) {
  const { open, setOpen } = useContext(MenuContext)!;
  return (
    <div onClick={() => setOpen(!open)} className={cn("cursor-pointer select-none outline-none", className)}>
      {children}
    </div>
  );
}

export function MenuPanel({ children, className, align = "left" }: { children: React.ReactNode, className?: string, align?: "left" | "right" | "top" }) {
  const { open } = useContext(MenuContext)!;
  const { theme } = useTheme();
  const isLight = theme === "light";

  if (!open) return null;

  return (
    <div className={cn(
      "absolute z-50 w-48 mt-2 rounded-2xl animate-fade-in py-1 overflow-hidden backdrop-blur-2xl border",
      isLight
        ? "bg-white/75 border-slate-200 shadow-lg"
        : "bg-[#141c38]/75 border-white/10 shadow-glass",
      align === "right" ? "right-0" : align === "top" ? "bottom-full left-0 mb-2" : "left-0",
      className
    )}>
      {children}
    </div>
  );
}

export function MenuItem({ children, onClick, className, variant = 'default' }: { children: React.ReactNode, onClick?: () => void, className?: string, variant?: 'default' | 'destructive' }) {
  const { setOpen } = useContext(MenuContext)!;

  return (
    <button
      onClick={(e) => {
        if (onClick) onClick();
        setOpen(false);
      }}
      className={cn(
        "w-full px-4 py-2 text-sm flex items-center gap-2 transition-colors outline-none",
        variant === 'destructive'
          ? "text-critical-300 hover:bg-white/5 hover:text-critical-400"
          : "text-slate-300 hover:bg-white/5 hover:text-white",
        className
      )}
    >
      {children}
    </button>
  );
}

export function MenuSeparator({ className }: { className?: string }) {
  const { theme } = useTheme();
  const isLight = theme === "light";
  return <div className={cn("h-px my-1 mx-2", isLight ? "bg-slate-200" : "bg-white/10", className)} />;
}
